/**
 * Pure helpers for the portals-parity chat: citation renumbering (run on `done`)
 * and transcript sectioning for the side-panel transcript view.
 *
 * Kept dependency-free and pure so they can be unit-tested in isolation.
 */
import type { Citation } from "../../../../store/state/chat/chatService";

// Match a single citation marker: [3]
const MARKER = /\[(\d+)\]/g;
// Match a grouped marker the model was told not to emit: [3, 5] or [3,5,7]
const GROUPED = /\[(\d+(?:\s*,\s*\d+)+)\]/g;

/**
 * Normalize + renumber an assistant turn so the visible references read 1,2,3…
 * in order of first appearance, and only cited sources survive.
 *
 * Steps (mirrors the portals spec §4.3):
 *  1. Split grouped markers `[3, 5]` → `[3][5]` (safety net).
 *  2. Collect cited indexes in first-appearance order.
 *  3. Build an old→new map (first cited → 1, next → 2, …).
 *  4. Rewrite the text and filter/renumber the citations array to match.
 *
 * If the text carries no markers at all (e.g. a turn that predates the marker
 * contract), the content and citations are returned unchanged so a fallback
 * sources list can still render.
 */
export function renumberCitations(content: string, citations: Citation[]): { content: string; citations: Citation[] } {
  const text = content.replace(GROUPED, (_full, group: string) =>
    group
      .split(",")
      .map((n) => `[${n.trim()}]`)
      .join("")
  );

  const order: number[] = [];
  const seen = new Set<number>();
  for (const m of text.matchAll(MARKER)) {
    const n = parseInt(m[1], 10);
    if (!seen.has(n)) {
      seen.add(n);
      order.push(n);
    }
  }

  // No markers → nothing to renumber; leave the turn untouched.
  if (order.length === 0) return { content, citations };

  // Look up a citation by its emitted index, falling back to array position.
  const byIndex = new Map<number, Citation>();
  citations.forEach((c, i) => byIndex.set(c.index ?? i + 1, c));

  const remap = new Map<number, number>();
  const out: Citation[] = [];
  for (const old of order) {
    const c = byIndex.get(old);
    if (!c) continue;
    const next = out.length + 1;
    remap.set(old, next);
    out.push({ ...c, index: next });
  }

  // If markers matched no known citations, keep the original turn.
  if (out.length === 0) return { content, citations };

  const rewritten = text.replace(MARKER, (whole, d: string) => {
    const n = remap.get(parseInt(d, 10));
    return n ? `[${n}]` : whole;
  });

  return { content: rewritten, citations: out };
}

// ----- Transcript sectioning (side-panel transcript view) -----
//
// Ports node's toSections (node/controllers/internal.ts) to the browser so the
// transcript view can render word-level text from GET /transcripts/:storyId,
// whose `transcript` is the EditableTranscript doc ({ words, paragraphs }).

export interface TWord {
  text: string;
  start: number;
  end: number;
}
export interface TParagraph {
  speaker: string;
  words: TWord[];
}
export interface TSection {
  title: string;
  paragraphs: TParagraph[];
}

function toWord(w: any): TWord {
  return { text: w?.text ?? "", start: w?.start ?? 0, end: w?.end ?? 0 };
}

/**
 * Normalize the stored transcript shapes into sections of paragraphs of words.
 *   1) sections format: words = { sections: [ { title, paragraphs: [ { speaker, words } ] } ] }
 *   2) legacy flat: words = Word[] with section_idx/para_idx, or a bare array.
 */
export function toSections(words: any, paragraphs: any): TSection[] {
  if (words && typeof words === "object" && !Array.isArray(words) && Array.isArray(words.sections)) {
    return (words.sections as any[]).map((s) => ({
      title: s?.title ?? "",
      paragraphs: (s?.paragraphs ?? []).map((p: any) => ({
        speaker: p?.speaker ?? "",
        words: (p?.words ?? []).map(toWord),
      })),
    }));
  }

  if (!Array.isArray(words) || words.length === 0) return [];
  const flat = words as any[];

  const hasIdx = flat.some((w) => typeof w.section_idx === "number" || typeof w.para_idx === "number");
  if (hasIdx) {
    const sectionMap = new Map<number, Map<number, TWord[]>>();
    for (const w of flat) {
      const sIdx = typeof w.section_idx === "number" ? w.section_idx : 0;
      const pIdx = typeof w.para_idx === "number" ? w.para_idx : 0;
      let paraMap = sectionMap.get(sIdx);
      if (!paraMap) sectionMap.set(sIdx, (paraMap = new Map()));
      let list = paraMap.get(pIdx);
      if (!list) paraMap.set(pIdx, (list = []));
      list.push(toWord(w));
    }
    const paraList = Array.isArray(paragraphs) ? (paragraphs as Array<{ speaker?: string }>) : [];
    let paraCounter = 0;
    return [...sectionMap.entries()]
      .sort(([a], [b]) => a - b)
      .map(([, paraMap]) => ({
        title: "",
        paragraphs: [...paraMap.entries()]
          .sort(([a], [b]) => a - b)
          .map(([, wordsInPara]) => {
            const speaker = paraList[paraCounter]?.speaker ?? "";
            paraCounter += 1;
            return { speaker, words: wordsInPara };
          }),
      }));
  }

  return [{ title: "", paragraphs: [{ speaker: "", words: flat.map(toWord) }] }];
}
